import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  OnOffBadge,
  RowMenu,
  SkeletonList,
  Switch,
  isForbidden,
  useAdminEditing,
} from '@/admin/AdminShell'
import { Icon } from '@/components/Icon'
import { ConfirmModal, StateView } from '@/card/record'
import { ApiError } from '@/lib/api'
import {
  createMailTemplate,
  deleteMailTemplate,
  fetchMailConnection,
  fetchMailTemplates,
  updateMailConnection,
  updateMailTemplate,
  type MailAnchor,
  type MailConnection,
  type MailTemplate,
  type MailVariable,
} from './api'
import { MailboxesPanel } from './MailboxesPanel'

/**
 * Почта: подключение SMTP и шаблоны писем (тикет #117).
 *
 * Два права на одном экране, и это не случайность: шаблоны — справочник раздела
 * (`system.settings`), подключение — учётные данные (`system.integration`, V37), и тот, кто
 * пишет тексты писем, не обязан видеть пароль почтового сервера. Каждая половина показывает
 * свой отказ словами, а не пустотой.
 *
 * Пароль только записывается: сервер отвечает, задан ли он, но не какой. Пустое поле при
 * сохранении оставляет прежний пароль, отдельная кнопка его стирает.
 */
const ANCHORS: MailAnchor[] = ['any', 'deal', 'counterparty', 'contact', 'invoice']

export function MailSettingsPage() {
  const { t, user } = useApp()
  const editing = useAdminEditing()
  const queryClient = useQueryClient()
  const templates = useQuery({ queryKey: ['admin-mail-templates'], queryFn: ({ signal }) => fetchMailTemplates(signal) })
  const [dialog, setDialog] = useState<MailTemplate | 'new' | null>(null)
  const [removing, setRemoving] = useState<MailTemplate | null>(null)

  const refresh = () => {
    editing.clear()
    void queryClient.invalidateQueries({ queryKey: ['admin-mail-templates'] })
    void queryClient.invalidateQueries({ queryKey: ['mail-templates'] })
  }
  const toggle = useMutation({
    mutationFn: (template: MailTemplate) => updateMailTemplate(template.id, { active: !template.active }),
    onSuccess: refresh,
    onError: editing.fail,
  })
  const remove = useMutation({
    mutationFn: (template: MailTemplate) => deleteMailTemplate(template.id),
    onSuccess: () => {
      setRemoving(null)
      refresh()
    },
    onError: (error) => {
      setRemoving(null)
      editing.fail(error)
    },
  })

  const list = templates.data?.templates ?? []
  return (
    <AdminPage
      titleKey="mail.adm.title"
      leadKey="mail.adm.lead"
      count={templates.data ? list.length : undefined}
      readOnly={!editing.mayEdit}
      forbidden={isForbidden(templates.error) && !can(user, 'system.integration', 'read')}
      primary={editing.mayEdit && !isForbidden(templates.error) ? (
        <button type="button" className="btn btn-primary" onClick={() => setDialog('new')}>
          <Icon name="plus" />{t('mail.adm.add')}
        </button>
      ) : undefined}
    >
      <ConnectionPanel />
      <MailboxesPanel />
      <Notice text={editing.notice} />
      <section className="stack" style={{ gap: 10 }} aria-label={t('mail.adm.templates')}>
        <span className="h2">{t('mail.adm.templates')}</span>
        <AdminState
          isLoading={templates.isLoading}
          error={templates.error}
          onRetry={() => void templates.refetch()}
          forbiddenTitleKey="mail.adm.forbidden"
          errorTitleKey="mail.adm.error"
          skeleton={<SkeletonList rows={4} />}
        >
          {list.length === 0 ? (
            <StateView kind="empty" boxed compact title={t('mail.adm.empty')} description={t('mail.adm.empty.text')} />
          ) : (
            <table className="table" aria-label={t('mail.adm.templates')}>
              <thead>
                <tr>
                  <th>{t('mail.adm.name')}</th>
                  <th>{t('mail.adm.anchor')}</th>
                  <th>{t('mail.subject')}</th>
                  <th>{t('mail.adm.state')}</th>
                  {editing.mayEdit && <th aria-label={t('mail.adm.actions')} />}
                </tr>
              </thead>
              <tbody>
                {list.map((template) => (
                  <tr key={template.id}>
                    <td className="strong">{template.name}</td>
                    <td>{t(`mail.anchor.${template.anchor}`)}</td>
                    <td className="mono t-sm">{template.subject}</td>
                    <td><OnOffBadge on={template.active} /></td>
                    {editing.mayEdit && (
                      <td>
                        <RowMenu label={t('mail.adm.actionsOf', { name: template.name })} items={[
                          { id: 'edit', label: t('mail.adm.edit'), icon: 'adm-edit', onSelect: () => setDialog(template) },
                          {
                            id: 'toggle',
                            label: template.active ? t('mail.adm.disable') : t('mail.adm.enable'),
                            icon: 'adm-archive',
                            onSelect: () => toggle.mutate(template),
                          },
                          { id: 'delete', label: t('mail.adm.delete'), icon: 'trash', danger: true, onSelect: () => setRemoving(template) },
                        ]} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </AdminState>
      </section>
      {dialog && (
        <TemplateDialog
          template={dialog === 'new' ? null : dialog}
          variables={templates.data?.variables ?? []}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null)
            refresh()
          }}
        />
      )}
      <ConfirmModal
        open={removing != null}
        title={t('mail.adm.delete.confirm', { name: removing?.name ?? '' })}
        confirmLabel={t('mail.adm.delete')}
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing)}
      >
        {t('mail.adm.delete.text')}
      </ConfirmModal>
    </AdminPage>
  )
}

function TemplateDialog({ template, variables, onClose, onSaved }: {
  template: MailTemplate | null
  variables: MailVariable[]
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const [name, setName] = useState(template?.name ?? '')
  const [anchor, setAnchor] = useState<MailAnchor>(template?.anchor ?? 'any')
  const [subject, setSubject] = useState(template?.subject ?? '')
  const [body, setBody] = useState(template?.body ?? '')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => {
      const fields = { name, anchor, subject, body, active: template?.active ?? true }
      return template ? updateMailTemplate(template.id, fields) : createMailTemplate(fields)
    },
    onSuccess: onSaved,
    // The server names the variable it refused; that sentence is the whole point of saving.
    onError: (failure) => setError(failure instanceof ApiError ? failure.message : t('app.error')),
  })
  const available = variables.filter((variable) => variable.anchors.includes(anchor)).map((variable) => variable.name)

  return (
    <EditLayer
      kind="sheet"
      wide
      overline={t('mail.adm.templates')}
      title={template ? t('mail.adm.edit') : t('mail.adm.add')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending} form={formId}
                            disabled={name.trim() === '' || subject.trim() === '' || body.trim() === ''} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('mail.adm.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <div className="form-grid">
          <Field label={t('mail.adm.name')} htmlFor={`${formId}-name`} required>
            <input id={`${formId}-name`} className="input" maxLength={128} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('mail.adm.anchor')} htmlFor={`${formId}-anchor`} help={t('mail.adm.anchor.help')}>
            <select id={`${formId}-anchor`} className="input" value={anchor} onChange={(e) => setAnchor(e.target.value as MailAnchor)}>
              {ANCHORS.map((code) => <option key={code} value={code}>{t(`mail.anchor.${code}`)}</option>)}
            </select>
          </Field>
        </div>
        <Field label={t('mail.subject')} htmlFor={`${formId}-subject`} required>
          <input id={`${formId}-subject`} className="input" maxLength={512} value={subject} onChange={(e) => setSubject(e.target.value)} />
        </Field>
        <Field label={t('mail.body')} htmlFor={`${formId}-body`} required>
          <textarea id={`${formId}-body`} className="input" rows={10} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <div className="stack" style={{ gap: 6 }} aria-label={t('mail.adm.variables')}>
          <span className="label">{t('mail.adm.variables')}</span>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {available.map((variable) => (
              <button key={variable} type="button" className="btn btn-ghost btn-sm mono"
                      onClick={() => setBody((current) => `${current}{{${variable}}}`)}>
                {`{{${variable}}}`}
              </button>
            ))}
          </div>
          <span className="t-sm muted">{t('mail.adm.variables.help')}</span>
        </div>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}

/** Подключение SMTP: своё право, свой отказ. */
function ConnectionPanel() {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const mayRead = can(user, 'system.integration', 'read')
  const mayEdit = can(user, 'system.integration', 'update')
  const connection = useQuery({
    queryKey: ['admin-mail-connection'],
    queryFn: ({ signal }) => fetchMailConnection(signal),
    enabled: mayRead,
  })
  const [draft, setDraft] = useState<MailConnection | null>(null)
  const [password, setPassword] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  useEffect(() => {
    if (connection.data) setDraft(connection.data)
  }, [connection.data])

  const save = useMutation({
    mutationFn: (clearPassword: boolean) => {
      const value = draft!
      return updateMailConnection({
        enabled: value.enabled,
        host: value.host?.trim() || null,
        port: value.port ?? null,
        security: value.security,
        username: value.username?.trim() || null,
        password: clearPassword ? '' : password === '' ? undefined : password,
        fromAddress: value.fromAddress?.trim() || null,
        fromName: value.fromName?.trim() || null,
      })
    },
    onSuccess: (value) => {
      setNotice(null)
      setPassword('')
      setSaved(true)
      queryClient.setQueryData(['admin-mail-connection'], value)
    },
    onError: (failure) => {
      setSaved(false)
      setNotice(failure instanceof ApiError ? failure.message : t('app.error'))
    },
  })

  if (!mayRead) {
    return <StateView kind="forbidden" compact title={t('mail.conn.forbidden')} description={t('mail.conn.forbidden.text')} />
  }
  if (connection.isLoading || !draft) {
    return connection.error
      ? <StateView kind="error" compact title={t('mail.conn.error')} />
      : <SkeletonList rows={3} />
  }
  const set = (patch: Partial<MailConnection>) => {
    setSaved(false)
    setDraft({ ...draft, ...patch })
  }
  const id = 'mail-conn'
  return (
    <section className="panel stack" style={{ gap: 12, marginBottom: 16 }} aria-label={t('mail.conn.title')}>
      <div className="row" style={{ gap: 8 }}>
        <span className="h2">{t('mail.conn.title')}</span>
        <OnOffBadge on={connection.data?.enabled ?? false} />
      </div>
      {!connection.data?.enabled && (
        <div className="alert alert-warning" role="note">
          <Icon name="alert" />
          <span>{t('mail.conn.off')}</span>
        </div>
      )}
      <form className="stack" style={{ gap: 12 }} aria-label={t('mail.conn.title')} onSubmit={(event) => {
        event.preventDefault()
        if (mayEdit) save.mutate(false)
      }}>
        <Switch checked={draft.enabled} disabled={!mayEdit} label={t('mail.conn.enabled')} onChange={(value) => set({ enabled: value })} />
        <div className="form-grid">
          <Field label={t('mail.conn.host')} htmlFor={`${id}-host`}>
            <input id={`${id}-host`} className="input" disabled={!mayEdit} value={draft.host ?? ''} onChange={(e) => set({ host: e.target.value })} />
          </Field>
          <Field label={t('mail.conn.port')} htmlFor={`${id}-port`}>
            <input id={`${id}-port`} className="input" type="number" disabled={!mayEdit} value={draft.port ?? ''}
                   onChange={(e) => set({ port: e.target.value === '' ? null : Number(e.target.value) })} />
          </Field>
          <Field label={t('mail.conn.security')} htmlFor={`${id}-security`}>
            <select id={`${id}-security`} className="input" disabled={!mayEdit} value={draft.security}
                    onChange={(e) => set({ security: e.target.value as MailConnection['security'] })}>
              {(['starttls', 'ssl', 'none'] as const).map((code) => <option key={code} value={code}>{t(`mail.conn.security.${code}`)}</option>)}
            </select>
          </Field>
          <Field label={t('mail.conn.username')} htmlFor={`${id}-user`}>
            <input id={`${id}-user`} className="input" disabled={!mayEdit} value={draft.username ?? ''} onChange={(e) => set({ username: e.target.value })} />
          </Field>
          <Field label={t('mail.conn.password')} htmlFor={`${id}-password`}
                 help={draft.passwordSet ? t('mail.conn.password.set') : t('mail.conn.password.unset')}>
            <input id={`${id}-password`} className="input" type="password" autoComplete="new-password" disabled={!mayEdit}
                   value={password} onChange={(e) => {
                     setSaved(false)
                     setPassword(e.target.value)
                   }} />
          </Field>
          <Field label={t('mail.conn.from')} htmlFor={`${id}-from`}>
            <input id={`${id}-from`} className="input" type="email" disabled={!mayEdit} value={draft.fromAddress ?? ''}
                   onChange={(e) => set({ fromAddress: e.target.value })} />
          </Field>
          <Field label={t('mail.conn.fromName')} htmlFor={`${id}-from-name`}>
            <input id={`${id}-from-name`} className="input" disabled={!mayEdit} value={draft.fromName ?? ''}
                   onChange={(e) => set({ fromName: e.target.value })} />
          </Field>
        </div>
        <Notice text={notice} />
        {saved && <span className="t-sm muted" role="status">{t('mail.conn.saved')}</span>}
        {mayEdit && (
          <div className="row" style={{ gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              {save.isPending ? t('adm.saving') : t('admin.save')}
            </button>
            {draft.passwordSet && (
              <button type="button" className="btn btn-ghost" disabled={save.isPending} onClick={() => save.mutate(true)}>
                {t('mail.conn.password.clear')}
              </button>
            )}
          </div>
        )}
      </form>
    </section>
  )
}
